import type {NormalizedCacheObject} from '@apollo/client';
import type {GetDownloadLinkRequestPayload} from '@igp/shared/api';
import {
    type ApolloClientProxy,
    type BaseHttpCodes,
    EntityBaseGqlService,
    type IDownloadByLinkService,
    type ServiceResponsePayload,
} from '@igp/shared/api';
import type {
    DebtResponsibleMasterAgent,
    Mutation,
    MutationAddDebtResponsibleMasterAgentArgs,
    MutationRemoveDebtResponsibleMasterAgentArgs,
    Query,
    QueryGetDebtResponsibleMasterAgentsArgs,
    Subscription,
} from '@igp/shared/contract/pam';
import type {IMessageLocalizer} from '@igp/shared/localization';
import {map} from '@igp/shared/otel';
import type {EntityFetchRequestPayload, EntityFetchServiceResponsePayload} from '@igp/shared/redux/entity';
import type {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {DebtResponsibleMasterAgentRequestBuilder} from './requestBuilder';
import type {
    AddDebtResponsibleMasterPayload,
    DebtResponsibleMasterAgentDownloadFields,
    DebtResponsibleMasterAgentQueryFields,
    DebtResponsibleMasterAgentQueryFilterKeys,
    RemoveDebtResponsibleMasterPayload,
} from './types';

export interface IDebtResponsibleMasterAgentsService {
    addDebtResponsibleMasterAgent(
        payload: AddDebtResponsibleMasterPayload
    ): Observable<ServiceResponsePayload<AddDebtResponsibleMasterPayload, DebtResponsibleMasterAgent>>;
    removeDebtResponsibleMasterAgent(
        payload: RemoveDebtResponsibleMasterPayload
    ): Observable<ServiceResponsePayload<RemoveDebtResponsibleMasterPayload, DebtResponsibleMasterAgent>>;
}

@injectable()
export class DebtResponsibleMasterAgentsService
    extends EntityBaseGqlService<
        EntityType,
        Query,
        QueryGetDebtResponsibleMasterAgentsArgs,
        DebtResponsibleMasterAgentQueryFields,
        Mutation,
        Subscription,
        BaseHttpCodes,
        DebtResponsibleMasterAgentQueryFilterKeys
    >
    implements IDebtResponsibleMasterAgentsService, IDownloadByLinkService<DebtResponsibleMasterAgentDownloadFields>
{
    private readonly _requestBuilder: DebtResponsibleMasterAgentRequestBuilder;

    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer
    ) {
        const builder = new DebtResponsibleMasterAgentRequestBuilder();
        super(client, builder, messageLocalizer);
        this._requestBuilder = builder;
    }

    get(
        requestPayload: EntityFetchRequestPayload<EntityType, DebtResponsibleMasterAgentQueryFields>
    ): Observable<EntityFetchServiceResponsePayload<EntityType, DebtResponsibleMasterAgent>> {
        return this.fetchData(requestPayload).pipe(
            map(r => ({
                ...r,
                requestPayload,
                responsePayload: {
                    items: r.responsePayload.getDebtResponsibleMasterAgents?.items ?? [],
                    total: r.responsePayload.getDebtResponsibleMasterAgents?.total_count ?? 0,
                },
            }))
        );
    }

    getDownloadLink(
        requestPayload: GetDownloadLinkRequestPayload<DebtResponsibleMasterAgentDownloadFields>
    ): Observable<ServiceResponsePayload<GetDownloadLinkRequestPayload<DebtResponsibleMasterAgentDownloadFields>, string>> {
        const request = this._requestBuilder.buildDownloadRequest(requestPayload);
        return this._service.query(request.query, request.variables).pipe(
            map(response => ({
                ...response,
                requestPayload,
                responsePayload: response?.responsePayload?.exportDebtResponsibleMasterAgents?.url,
            }))
        );
    }

    addDebtResponsibleMasterAgent({
        agent_id,
    }: AddDebtResponsibleMasterPayload): Observable<ServiceResponsePayload<AddDebtResponsibleMasterPayload, DebtResponsibleMasterAgent>> {
        const mutation = this._requestBuilder.buildAddDebtResponsibleMasterAgentMutation();
        return this._service
            .mutate<MutationAddDebtResponsibleMasterAgentArgs>(mutation, {agent_id})
            .pipe(map(r => ({...r, requestPayload: {agent_id}, responsePayload: r.responsePayload?.addDebtResponsibleMasterAgent})));
    }

    removeDebtResponsibleMasterAgent({
        agent_id,
    }: RemoveDebtResponsibleMasterPayload): Observable<
        ServiceResponsePayload<RemoveDebtResponsibleMasterPayload, DebtResponsibleMasterAgent>
    > {
        const mutation = this._requestBuilder.buildRemoveDebtResponsibleMasterAgentMutation();
        return this._service
            .mutate<MutationRemoveDebtResponsibleMasterAgentArgs>(mutation, {agent_id})
            .pipe(map(r => ({...r, requestPayload: {agent_id}, responsePayload: r.responsePayload?.removeDebtResponsibleMasterAgent})));
    }

    protected getErrorSeverity(): number {
        return 0;
    }
}
